/* __next_internal_client_entry_do_not_use__ PaginationBase auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import range from "lodash/range";
import * as React from "react";
import { MOBILE_NEXT_LINK_MARGIN, NavigationButton, NEXT_LINK_MARGIN, NEXT_LINK_SIZE, PAGE_LINK_MARGIN, PAGE_LINK_SIZE, IconPageLink, PageWrapper, Wrapper } from "./styles";
import { MIDDLE_PAGE_LINK_LOWER_BOUND, PAGE_LINK_COUNT_MAXIMUM, PAGE_LINK_COUNT_MINIMUM } from "./utils";
/**
 * @link [Docs](https://slate.faire.team/slate/component/pagination)
 * @trackcomponent
 */ export var PaginationBase = function(props) {
    var container = React.useRef(null);
    var _React_useState = _sliced_to_array(React.useState(0), 2), maxDisplayablePages = _React_useState[0], setMaxDisplayablePages = _React_useState[1];
    var isMobile = useViewport().isMobile;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var resizeObserver = global["ResizeObserver"] ? new ResizeObserver(function(_entries) {
        if (container.current) {
            computeMaxDisplayablePages(container.current.offsetWidth);
        }
    }) : undefined;
    /**
     * Determine how many page links will fit into a space that is `width` pixels
     * wide. This reserves space for the "back" and "next" links, plus as many
     * page links as will fit between them, up to a given maximum.
     */ var computeMaxDisplayablePages = function(width) {
        if (width === 0) {
            return;
        }
        if (!resizeObserver) {
            setMaxDisplayablePages(PAGE_LINK_COUNT_MINIMUM);
            return;
        }
        var pageLinkSpaceNeeded = PAGE_LINK_SIZE + PAGE_LINK_MARGIN * 2;
        var backNextLinkCombinedSpaceNeeded = (NEXT_LINK_SIZE + NEXT_LINK_MARGIN * 2) * 2;
        var mobileBackNextLinkCombinedSpaceNeeded = (NEXT_LINK_SIZE + MOBILE_NEXT_LINK_MARGIN * 2) * 2;
        var max = Math.min(PAGE_LINK_COUNT_MAXIMUM, Math.max(PAGE_LINK_COUNT_MINIMUM, Math.floor((width - (isMobile ? mobileBackNextLinkCombinedSpaceNeeded : backNextLinkCombinedSpaceNeeded)) / pageLinkSpaceNeeded)));
        if (max !== maxDisplayablePages) {
            setMaxDisplayablePages(max);
        }
    };
    React.useEffect(function() {
        var ref = container.current;
        if (container.current) {
            computeMaxDisplayablePages(container.current.offsetWidth);
        }
        return function() {
            if (ref) {
                var _resizeObserver_observe;
                resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
                resizeObserver === null || resizeObserver === void 0 ? void 0 : (_resizeObserver_observe = resizeObserver.observe) === null || _resizeObserver_observe === void 0 ? void 0 : _resizeObserver_observe.call(resizeObserver, ref);
            }
        };
    });
    var handleChangePage = function(page) {
        var _props_onChangePage;
        return (_props_onChangePage = props.onChangePage) === null || _props_onChangePage === void 0 ? void 0 : _props_onChangePage.call(props, page);
    };
    var _props_paginationData_page_count;
    var pageCount = (_props_paginationData_page_count = props.paginationData.page_count) !== null && _props_paginationData_page_count !== void 0 ? _props_paginationData_page_count : 0;
    var pages = range(1, pageCount + 1);
    var requiredPagesForDisplay = props.showWhenSinglePage ? 1 : 2;
    var _props_paginationData_page_number;
    var currentPage = (_props_paginationData_page_number = props.paginationData.page_number) !== null && _props_paginationData_page_number !== void 0 ? _props_paginationData_page_number : 1;
    var middlePageLinks = function() {
        if (maxDisplayablePages === undefined) {
            return [];
        }
        // This accounts for ellipses and the first/last pages on both sides.
        var middleDisplayablePages = maxDisplayablePages - 4;
        // Round to an odd number so that the current page is always centered.
        var displayablePagesToOneSide = Math.floor((middleDisplayablePages - 1) / 2);
        var numberOfItems = Math.max(1, 1 + displayablePagesToOneSide * 2);
        // Note that this lower bound represents the *third* element in the
        // list of pages; the minimum page that will be displayed in the
        // middle section is page 3.
        var lowerBound = MIDDLE_PAGE_LINK_LOWER_BOUND;
        var upperBound = pages.length - lowerBound;
        // The smallest page in the middle section
        // is at least 3 and at most the total number of
        // items away from the second-last page (so that when the current
        // page gets near the last page the minimum bound won't keep
        // creeping upward.)
        var sliceFrom = Math.max(Math.min(currentPage - displayablePagesToOneSide - 1, upperBound - numberOfItems), lowerBound);
        var sliceTo = Math.max(Math.min(sliceFrom + numberOfItems, upperBound), lowerBound);
        return pages.slice(sliceFrom, sliceTo);
    }();
    var _pages_;
    var firstPageNumber = (_pages_ = pages[0]) !== null && _pages_ !== void 0 ? _pages_ : 0;
    var backButtonPageNumber = currentPage === firstPageNumber ? currentPage : currentPage - 1;
    var _pages_1;
    var lastPageNumber = (_pages_1 = pages[pages.length - 1]) !== null && _pages_1 !== void 0 ? _pages_1 : 0;
    var nextButtonPageNumber = currentPage === lastPageNumber ? currentPage : currentPage + 1;
    var isButtonDisabled = function(page) {
        var disableButton = props.disableButton;
        if (typeof disableButton === "boolean") {
            return disableButton;
        }
        var _disableButton;
        return (_disableButton = disableButton === null || disableButton === void 0 ? void 0 : disableButton(page)) !== null && _disableButton !== void 0 ? _disableButton : false;
    };
    var isBackButtonDisabled = currentPage === firstPageNumber || isButtonDisabled(backButtonPageNumber);
    var isNextButtonDisabled = currentPage === lastPageNumber || isButtonDisabled(nextButtonPageNumber);
    var pageToLink = props.pageToLink;
    if (pages.length < requiredPagesForDisplay) {
        return null;
    }
    var renderPageLinks = function() {
        var pageToLink = props.pageToLink;
        if (pages.length <= PAGE_LINK_COUNT_MINIMUM) {
            return /*#__PURE__*/ _jsx(PageWrapper, {
                children: pages.map(function(option) {
                    return /*#__PURE__*/ _jsx(IconPageLink, {
                        to: pageToLink(option),
                        active: currentPage === option,
                        onClick: function() {
                            return handleChangePage(option);
                        },
                        disabled: isButtonDisabled(option),
                        children: option
                    }, option);
                })
            });
        }
        if (maxDisplayablePages === undefined) {
            return null;
        }
        var _pages_;
        var secondPageNumber = (_pages_ = pages[1]) !== null && _pages_ !== void 0 ? _pages_ : 0;
        var _pages_1;
        var secondLastPageNumber = (_pages_1 = pages[pages.length - 2]) !== null && _pages_1 !== void 0 ? _pages_1 : 0;
        // True if the middle set of links starts at page 3,
        // so the link to the second page should be active
        // instead of being replaced with an ellipsis (...).
        var isFirstContiguous = middlePageLinks[0] === pages[2];
        var isLastContiguous = middlePageLinks[middlePageLinks.length - 1] === pages[pages.length - 3];
        return /*#__PURE__*/ _jsxs(PageWrapper, {
            children: [
                /*#__PURE__*/ _jsx(IconPageLink, {
                    to: pageToLink(firstPageNumber),
                    active: currentPage === firstPageNumber,
                    onClick: function() {
                        return handleChangePage(firstPageNumber);
                    },
                    disabled: isButtonDisabled(firstPageNumber),
                    children: firstPageNumber
                }),
                /*#__PURE__*/ _jsx(IconPageLink, {
                    active: currentPage === secondPageNumber,
                    to: pageToLink(secondPageNumber),
                    onClick: function() {
                        return handleChangePage(secondPageNumber);
                    },
                    disabled: !isFirstContiguous || isButtonDisabled(secondPageNumber),
                    children: !isFirstContiguous ? "..." : secondPageNumber
                }),
                middlePageLinks.map(function(option) {
                    return /*#__PURE__*/ _jsx(IconPageLink, {
                        to: pageToLink(option),
                        active: currentPage === option,
                        onClick: function() {
                            return handleChangePage(option);
                        },
                        disabled: isButtonDisabled(option),
                        children: option
                    }, option);
                }),
                /*#__PURE__*/ _jsx(IconPageLink, {
                    to: pageToLink(secondLastPageNumber),
                    active: currentPage === secondLastPageNumber,
                    onClick: function() {
                        return handleChangePage(secondLastPageNumber);
                    },
                    disabled: !isLastContiguous || isButtonDisabled(secondLastPageNumber),
                    children: !isLastContiguous ? "..." : secondLastPageNumber
                }),
                /*#__PURE__*/ _jsx(IconPageLink, {
                    to: pageToLink(lastPageNumber),
                    active: currentPage === lastPageNumber,
                    onClick: function() {
                        return handleChangePage(lastPageNumber);
                    },
                    disabled: isButtonDisabled(lastPageNumber),
                    children: lastPageNumber
                })
            ]
        });
    };
    return /*#__PURE__*/ _jsxs(Wrapper, {
        ref: container,
        "data-test-id": "icon-pagination-wrapper",
        className: props.className,
        children: [
            /*#__PURE__*/ _jsx(NavigationButton, {
                disabled: isBackButtonDisabled,
                linkTo: pageToLink(backButtonPageNumber),
                variant: "secondary",
                onClick: function() {
                    return handleChangePage(currentPage - 1);
                },
                "aria-label": strictLocalize({
                    id: "epG7uF",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Previous page"
                        }
                    ]
                }),
                children: /*#__PURE__*/ _jsx(Arrow, {
                    direction: "left"
                })
            }),
            renderPageLinks(),
            /*#__PURE__*/ _jsx(NavigationButton, {
                disabled: isNextButtonDisabled,
                linkTo: pageToLink(nextButtonPageNumber),
                variant: "secondary",
                onClick: function() {
                    return handleChangePage(currentPage + 1);
                },
                "aria-label": strictLocalize({
                    id: "Cqo6wj",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Next page"
                        }
                    ]
                }),
                children: /*#__PURE__*/ _jsx(Arrow, {
                    direction: "right"
                })
            })
        ]
    });
};
